import { useState, useRef, useCallback } from "react";
import axios from "axios";
import { API, getAuthHeaders } from "../../App";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Camera, Mic, MicOff, Trash2, Image as ImageIcon, FileAudio, Loader2, X } from "lucide-react";
import { toast } from "sonner";

export function WOAttachmentsButton({ woId, canEdit }) {
  const [open, setOpen] = useState(false);
  const [photos, setPhotos] = useState([]);
  const [voiceNotes, setVoiceNotes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [recording, setRecording] = useState(false);
  const [caption, setCaption] = useState("");
  const photoRef = useRef(null);
  const mediaRecorderRef = useRef(null);
  const chunksRef = useRef([]);

  const fetchAttachments = useCallback(async () => {
    setLoading(true);
    try {
      const [photosResp, notesResp] = await Promise.all([
        axios.get(`${API}/workorders/${woId}/photos`, { headers: getAuthHeaders() }),
        axios.get(`${API}/workorders/${woId}/voice-notes`, { headers: getAuthHeaders() }),
      ]);
      setPhotos(photosResp.data || []);
      setVoiceNotes(notesResp.data || []);
    } catch { /* silent */ }
    finally { setLoading(false); }
  }, [woId]);

  const handleOpen = () => { setOpen(true); fetchAttachments(); };

  const handlePhotoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append("file", file);
      formData.append("caption", caption);
      await axios.post(`${API}/workorders/${woId}/photos`, formData, {
        headers: { ...getAuthHeaders(), "Content-Type": "multipart/form-data" },
      });
      toast.success("Photo uploaded");
      setCaption("");
      fetchAttachments();
    } catch (err) { toast.error(err.response?.data?.detail || "Upload failed"); }
    finally { setUploading(false); if (photoRef.current) photoRef.current.value = ""; }
  };

  const handleDeletePhoto = async (photoId) => {
    if (!window.confirm("Delete this photo?")) return;
    try {
      await axios.delete(`${API}/workorders/${woId}/photos/${photoId}`, { headers: getAuthHeaders() });
      toast.success("Photo deleted");
      fetchAttachments();
    } catch { toast.error("Failed to delete"); }
  };

  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const recorder = new MediaRecorder(stream, { mimeType: "audio/webm" });
      chunksRef.current = [];
      recorder.ondataavailable = (e) => { if (e.data.size > 0) chunksRef.current.push(e.data); };
      recorder.onstop = async () => {
        stream.getTracks().forEach(t => t.stop());
        const blob = new Blob(chunksRef.current, { type: "audio/webm" });
        await uploadVoiceNote(blob);
      };
      recorder.start();
      mediaRecorderRef.current = recorder;
      setRecording(true);
    } catch (err) {
      toast.error("Microphone access denied");
    }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current && mediaRecorderRef.current.state === "recording") {
      mediaRecorderRef.current.stop();
    }
    setRecording(false);
  };

  const uploadVoiceNote = async (blob) => {
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append("file", blob, `voice_${Date.now()}.webm`);
      const resp = await axios.post(`${API}/workorders/${woId}/voice-notes`, formData, {
        headers: { ...getAuthHeaders(), "Content-Type": "multipart/form-data" },
      });
      const transcript = resp.data?.transcript || "";
      toast.success(transcript.startsWith("[") ? "Voice note saved (transcription pending)" : "Voice note transcribed");
      fetchAttachments();
    } catch (err) { toast.error(err.response?.data?.detail || "Upload failed"); }
    finally { setUploading(false); }
  };

  const handleDeleteVoice = async (noteId) => {
    if (!window.confirm("Delete this voice note?")) return;
    try {
      await axios.delete(`${API}/workorders/${woId}/voice-notes/${noteId}`, { headers: getAuthHeaders() });
      toast.success("Voice note deleted");
      fetchAttachments();
    } catch { toast.error("Failed to delete"); }
  };

  const totalCount = photos.length + voiceNotes.length;

  return (
    <>
      <Button size="sm" variant="outline" onClick={handleOpen} className="rounded-none border-[#E7E5E4] relative" data-testid={`attachments-wo-${woId}`}>
        <Camera size={14} />
        {totalCount > 0 && (
          <span className="absolute -top-1.5 -right-1.5 bg-[#2563EB] text-white text-[9px] w-4 h-4 flex items-center justify-center font-bold">{totalCount}</span>
        )}
      </Button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-white rounded-none border border-[#E2E8F0] max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="wo-attachments-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading text-[#0F172A] flex items-center gap-2">
              <Camera size={18} /> Photos & Voice Notes
            </DialogTitle>
            <DialogDescription className="text-xs text-[#64748B]">
              Attach photos from camera/gallery and record voice notes with automatic transcription.
            </DialogDescription>
          </DialogHeader>

          {loading ? (
            <div className="flex items-center justify-center py-8"><Loader2 className="animate-spin text-[#64748B]" size={24} /></div>
          ) : (
            <div className="space-y-4">
              {/* Upload Controls */}
              {canEdit && (
                <div className="flex gap-2 items-end">
                  <div className="flex-1">
                    <input
                      ref={photoRef}
                      type="file"
                      accept="image/*"
                      capture="environment"
                      onChange={handlePhotoUpload}
                      className="hidden"
                      data-testid="photo-file-input"
                    />
                    <Button
                      onClick={() => photoRef.current?.click()}
                      disabled={uploading}
                      className="rounded-none bg-[#2563EB] text-white w-full"
                      data-testid="upload-photo-btn"
                    >
                      {uploading ? <Loader2 size={14} className="animate-spin mr-1" /> : <Camera size={14} className="mr-1" />}
                      {uploading ? "Uploading..." : "Take / Upload Photo"}
                    </Button>
                  </div>
                  <Button
                    onClick={recording ? stopRecording : startRecording}
                    disabled={uploading}
                    className={`rounded-none w-36 ${recording ? "bg-[#DC2626] hover:bg-[#B91C1C]" : "bg-[#D97706] hover:bg-[#B45309]"} text-white`}
                    data-testid="voice-record-btn"
                  >
                    {recording ? <><MicOff size={14} className="mr-1" /> Stop</> : <><Mic size={14} className="mr-1" /> Record</>}
                  </Button>
                </div>
              )}

              {recording && (
                <div className="bg-[#FEF2F2] border border-[#DC2626] p-3 flex items-center gap-2 animate-pulse" data-testid="recording-indicator">
                  <div className="w-3 h-3 bg-[#DC2626] rounded-full" />
                  <span className="text-sm font-bold text-[#DC2626]">Recording... Tap Stop when done</span>
                </div>
              )}

              {/* Photos */}
              {photos.length > 0 && (
                <div>
                  <h3 className="text-xs font-bold text-[#0F172A] uppercase tracking-wider mb-2 flex items-center gap-1">
                    <ImageIcon size={14} /> Photos ({photos.length})
                  </h3>
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                    {photos.map((p) => (
                      <div key={p.id} className="border border-[#E2E8F0] relative group" data-testid={`photo-${p.id}`}>
                        <img
                          src={`${API}/workorders/${woId}/photos/${p.id}/image`}
                          alt={p.caption || p.filename}
                          className="w-full h-32 object-cover"
                          loading="lazy"
                        />
                        <div className="p-2">
                          {p.caption && <p className="text-xs text-[#0F172A] font-medium truncate">{p.caption}</p>}
                          <p className="text-[9px] text-[#94A3B8]">{p.uploaded_by_name} · {new Date(p.created_at).toLocaleString()}</p>
                        </div>
                        {canEdit && (
                          <button onClick={() => handleDeletePhoto(p.id)} className="absolute top-1 right-1 bg-[#DC2626] text-white p-1 opacity-0 group-hover:opacity-100 transition-opacity" data-testid={`delete-photo-${p.id}`}>
                            <X size={12} />
                          </button>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Voice Notes */}
              {voiceNotes.length > 0 && (
                <div>
                  <h3 className="text-xs font-bold text-[#0F172A] uppercase tracking-wider mb-2 flex items-center gap-1">
                    <FileAudio size={14} /> Voice Notes ({voiceNotes.length})
                  </h3>
                  <div className="space-y-2">
                    {voiceNotes.map((n) => (
                      <div key={n.id} className="border border-[#E2E8F0] p-3" data-testid={`voice-note-${n.id}`}>
                        <div className="flex items-start justify-between gap-2">
                          <div className="flex-1 min-w-0">
                            <audio controls className="w-full h-8 mb-1" data-testid={`audio-player-${n.id}`}>
                              <source src={`${API}/workorders/${woId}/voice-notes/${n.id}/audio`} type={n.content_type || "audio/webm"} />
                            </audio>
                            {n.transcript && !n.transcript.startsWith("[") && (
                              <p className="text-xs text-[#0F172A] bg-[#F8FAFC] p-2 border-l-2 border-[#2563EB] mt-1" data-testid={`transcript-${n.id}`}>
                                {n.transcript}
                              </p>
                            )}
                            {n.transcript && n.transcript.startsWith("[") && (
                              <p className="text-xs text-[#94A3B8] italic mt-1">{n.transcript}</p>
                            )}
                            <p className="text-[9px] text-[#94A3B8] mt-1">{n.uploaded_by_name} · {new Date(n.created_at).toLocaleString()}</p>
                          </div>
                          {canEdit && (
                            <button onClick={() => handleDeleteVoice(n.id)} className="text-[#DC2626] p-1 hover:bg-[#FEF2F2]" data-testid={`delete-voice-${n.id}`}>
                              <Trash2 size={14} />
                            </button>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {photos.length === 0 && voiceNotes.length === 0 && (
                <div className="text-center py-6 text-sm text-[#94A3B8]">
                  <Camera size={32} className="mx-auto mb-2 text-[#CBD5E1]" />
                  No attachments yet. Use the buttons above to add photos or voice notes.
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}
